import type { FC } from 'react';
import { useRef } from 'react';

import { Collapsible as PrimitiveCollapsible } from '@base-ui/react/collapsible';

import type { ClassNameProps } from '@tripleten/cn';

import { Slot } from '../../../primitives/slot/slot.tsx';
import { cnCollapsible } from '../collapsible.constants.ts';
import { useClipped } from '../collapsible.hooks/use-clipped.ts';
import { useContentClipping } from '../collapsible.hooks/use-content-clipping.ts';

import './collapsible__panel.css';

import './_clipped/collapsible__panel_clipped.css';
import './_partial/collapsible__panel_partial.css';

/**
 * Props for the Collapsible.Panel component.
 */
export type CollapsiblePanelProps = ClassNameProps &
    Omit<PrimitiveCollapsible.Panel.Props, 'hiddenUntilFound' | 'keepMounted'> &
    (
        | {
              partial?: false;
              hiddenUntilFound?: boolean;
              keepMounted?: boolean;
          }
        | {
              /**
               * Collapses to `--collapsible-panel-collapsed-height` instead of to nothing, fading out
               * the cut. Set that variable in CSS.
               */
              partial: true;
              hiddenUntilFound?: never;
              keepMounted?: never;
          }
    );

/**
 * The container of the collapsible content. Animates open/close using a height transition
 * driven by `--collapsible-panel-height`. Pass `keepMounted` to keep the children in the DOM
 * while the panel is closed, or `hiddenUntilFound` to also let the browser's page search find them.
 *
 * ```tsx
 * <Collapsible.Panel keepMounted>
 *     <Text>Content to show or hide.</Text>
 * </Collapsible.Panel>
 * ```
 *
 * ```tsx
 * <Collapsible.Panel partial>
 *     <Text>A long piece of reading material.</Text>
 * </Collapsible.Panel>
 * ```
 */
export const CollapsiblePanel: FC<CollapsiblePanelProps> = ({ className, partial, ...props }) => {
    const clipped = useClipped();
    const ref = useRef<HTMLDivElement>(null);

    useContentClipping({ ref, partial });

    return (
        <Slot ref={ref}>
            <PrimitiveCollapsible.Panel
                {...props}
                {...(partial
                    ? // Collapsed still shows a slice, so the panel must exist and stay rendered while closed.
                      { keepMounted: true, hidden: false }
                    : undefined)}
                className={cnCollapsible('panel', { partial, clipped }, [className])}
            />
        </Slot>
    );
};
